import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@devhop/ui";
import { Banknote, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useActiveRateQuery,
  useCreateRate,
  useRateHistoryQuery,
} from "../api/queries";

type Props = {
  /** ແຖວຂ້າງ template ໃນ desktop */
  compact?: boolean;
};

export function CurrencyRatesCard({ compact = false }: Props) {
  const active = useActiveRateQuery();
  const history = useRateHistoryQuery();
  const create = useCreateRate();

  const [baht, setBaht] = useState("");
  const [usd, setUsd] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (active.data?.rate) {
      setBaht(String(active.data.rate.rateKipToBaht));
      setUsd(String(active.data.rate.rateKipToUsd));
    }
  }, [active.data]);

  const handleSubmit = () => {
    const b = Number(baht);
    const u = Number(usd);
    if (!Number.isFinite(b) || b <= 0) return;
    if (!Number.isFinite(u) || u <= 0) return;
    create.mutate(
      { rateKipToBaht: b, rateKipToUsd: u, notes: notes.trim() || null },
      {
        onSuccess: () => setNotes(""),
      },
    );
  };

  const activeRate = active.data?.rate;
  const rows = history.data?.history ?? [];

  const form = (
    <div
      className={
        compact
          ? "space-y-3"
          : "grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_2fr_auto] sm:items-end"
      }
    >
      <div className="space-y-1">
        <Label className="text-xs">Baht ຕໍ່ 1 ກີບ</Label>
        <Input
          inputMode="decimal"
          value={baht}
          onChange={(e) => setBaht(e.target.value)}
          placeholder="0.001587"
        />
        {baht && Number(baht) > 0 ? (
          <p className="text-[11px] text-muted-foreground">
            1 ບາດ ≈ {Math.round(1 / Number(baht)).toLocaleString("lo-LA")} ກີບ
          </p>
        ) : null}
      </div>
      <div className="space-y-1">
        <Label className="text-xs">USD ຕໍ່ 1 ກີບ</Label>
        <Input
          inputMode="decimal"
          value={usd}
          onChange={(e) => setUsd(e.target.value)}
          placeholder="0.000045"
        />
        {usd && Number(usd) > 0 ? (
          <p className="text-[11px] text-muted-foreground">
            1 USD ≈ {Math.round(1 / Number(usd)).toLocaleString("lo-LA")} ກີບ
          </p>
        ) : null}
      </div>
      <div className="space-y-1">
        <Label className="text-xs">ໝາຍເຫດ (ທາງເລືອກ)</Label>
        <Textarea
          rows={compact ? 2 : 2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="ເຊັ່ນ: ປັບຕາມຕະຫຼາດ"
        />
      </div>
      <Button
        type="button"
        className={compact ? "w-full" : undefined}
        onClick={handleSubmit}
        disabled={create.isPending}
      >
        <span className="inline-flex items-center gap-2">
          <Save className="size-4" aria-hidden />
          ບັນທຶກອັດຕາໃໝ່
        </span>
      </Button>
    </div>
  );

  const historyTable = (
    <Accordion type="single" collapsible>
      <AccordionItem value="history" className="border-none">
        <AccordionTrigger className="py-2 text-sm hover:no-underline">
          ປະຫວັດ ({rows.length})
        </AccordionTrigger>
        <AccordionContent>
          <div className="max-h-[240px] overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">ວັນທີ</TableHead>
                  <TableHead className="text-right text-xs">Baht</TableHead>
                  <TableHead className="text-right text-xs">USD</TableHead>
                  <TableHead className="text-center text-xs">ສະຖານະ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground text-xs"
                    >
                      ຍັງບໍ່ມີປະຫວັດ
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">
                        {formatDateTimeLocal(r.createdAt)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {r.rateKipToBaht}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {r.rateKipToUsd}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.isActive ? (
                          <Badge variant="default" className="text-[10px]">
                            ໃຊ້ງານ
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-[10px]">
                            ປະຫວັດ
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <Card className={compact ? "xl:sticky xl:top-4" : undefined}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Banknote className="size-4 text-primary" aria-hidden />
          ອັດຕາແລກປ່ຽນ
        </CardTitle>
        <p className="text-muted-foreground text-xs">
          ສຳລັບ {"{{summaryPriceBaht}}"} ແລະ {"{{summaryPriceUsd}}"} ໃນ template
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {form}

        {activeRate ? (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-muted-foreground text-xs">
            ປັດຈຸບັນ: {activeRate.rateKipToBaht} Baht/Kip ·{" "}
            {activeRate.rateKipToUsd} USD/Kip
            {activeRate.notes ? ` — ${activeRate.notes}` : ""}
          </div>
        ) : null}

        {historyTable}
      </CardContent>
    </Card>
  );
}
